import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, Children } from "react";
import assert from "assert";
import { Box } from "../Box/Box.mjs";
import { Text } from "../Text/Text.mjs";
import { buildDataAttributes } from "../private/buildDataAttributes.mjs";
import { Bleed } from "../Bleed/Bleed.mjs";
const validTones = ["promote", "info", "neutral", "positive", "caution", "critical"];
const variantMap = {
  solid: {
    promote: {
      background: "promote"
    },
    info: {
      background: "info"
    },
    neutral: {
      background: "neutral"
    },
    positive: {
      background: "positive"
    },
    caution: {
      background: "caution"
    },
    critical: {
      background: "critical"
    }
  },
  soft: {
    promote: {
      background: "promoteLight"
    },
    info: {
      background: "infoLight"
    },
    neutral: {
      background: "neutralLight"
    },
    positive: {
      background: "positiveLight"
    },
    caution: {
      background: "cautionLight"
    },
    critical: {
      background: "criticalLight"
    }
  },
  ghost: {
    promote: {
      tone: "promote",
      background: "body",
      boxShadow: "borderPromoteLight"
    },
    info: {
      tone: "info",
      background: "body",
      boxShadow: "borderInfoLight"
    },
    neutral: {
      tone: "neutral",
      background: "body",
      boxShadow: "borderNeutralLight"
    },
    positive: {
      tone: "positive",
      background: "body",
      boxShadow: "borderPositiveLight"
    },
    caution: {
      tone: "caution",
      background: "body",
      boxShadow: "borderCautionLight"
    },
    critical: {
      tone: "critical",
      background: "body",
      boxShadow: "borderCriticalLight"
    }
  }
};
const stringifyChildren = (children) => {
  if (typeof children === "string") {
    return children;
  }
  if (typeof children === "number") {
    return children.toString();
  }
  return children.join("");
};
const Badge = forwardRef(
  ({
    tone = "info",
    variant = "soft",
    bleedY = false,
    title,
    children,
    id,
    data,
    tabIndex,
    "aria-describedby": ariaDescribedBy,
    icon,
    ...restProps
  }, ref) => {
    assert(validTones.indexOf(tone) >= 0, `Badge tone of "${tone}" is not valid.`);
    assert(
      Children.toArray(children).every((child) => ["string", "number"].includes(typeof child)),
      "Badge may only contain strings or numbers"
    );
    const stylesForVariant = variantMap[variant][tone];
    const content = /* @__PURE__ */ jsx(
      Box,
      {
        component: "span",
        display: "flex",
        cursor: "default",
        ...buildDataAttributes({ data, validateRestProps: restProps }),
        children: /* @__PURE__ */ jsxs(
          Box,
          {
            component: "span",
            id,
            ref,
            tabIndex,
            "aria-describedby": ariaDescribedBy,
            title: title ?? (!ariaDescribedBy ? stringifyChildren(children) : void 0),
            background: stylesForVariant.background,
            padding: "xxsmall",
            borderRadius: "xsmall",
            position: "relative",
            children: [
              stylesForVariant.boxShadow && /* @__PURE__ */ jsx(
                Box,
                {
                  component: "span",
                  boxShadow: stylesForVariant.boxShadow,
                  borderRadius: "xsmall",
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none"
                }
              ),
              /* @__PURE__ */ jsxs(Text, { tone: stylesForVariant.tone, size: "xsmall", weight: "medium", maxLines: 1, children: [
                icon,
                " ",
                children
              ] })
            ]
          }
        )
      }
    );
    return bleedY ? /* @__PURE__ */ jsx(Bleed, { component: "span", vertical: "xxsmall", children: content }) : content;
  }
);
Badge.displayName = "Badge";
Badge.__isBadge__ = true;
export {
  Badge
};
